/**
 * PictView-Retold-Login
 *
 * The SHARED sign-in view. Renders into #App-Container ahead of the shell when the app has
 * auth enabled and there is no session. On success it calls the OnAuthenticated callback
 * the base app supplies, which swaps in the shell.
 *
 * When two-step login is on (the app sets TwoStepLogin, from the server manifest), a
 * password that belongs to a two-step account returns a challenge instead of a session:
 * the view then shows a code card, and verifying the code completes the sign-in. With
 * two-step off, or for an account without it, sign-in is a single step as before.
 *
 * An app can replace this by registering its own view and pointing loginViewHash() at it.
 *
 * Theme: every themed background is paired with a themed foreground, and inputs and
 * buttons set both as a pair, so the view reads in light and dark and degrades to sane
 * light fallbacks in an unthemed host.
 */

const libPictView = require('pict-view');

class PictViewRetoldLogin extends libPictView
{
	_authClient() { return this.pict.providers['Retold-AuthClient']; }

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap && typeof this.pict.CSSMap.injectCSS === 'function') { this.pict.CSSMap.injectCSS(); }
		this._decorate();
		this._wire();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	_decorate()
	{
		if (typeof document === 'undefined') { return; }
		let tmpTitle = document.getElementById('Retold-Login-Title');
		if (tmpTitle)
		{
			let tmpBrand = this.options.Brand || {};
			tmpTitle.textContent = tmpBrand.Name ? ('Sign in to ' + tmpBrand.Name) : 'Sign in';
		}
	}

	_wire()
	{
		if (typeof document === 'undefined') { return; }
		let tmpForm = document.getElementById('Retold-Login-Form');
		if (tmpForm && tmpForm.getAttribute('data-retold-wired') !== '1')
		{
			tmpForm.setAttribute('data-retold-wired', '1');
			tmpForm.addEventListener('submit', (pEvent) => { if (pEvent && pEvent.preventDefault) { pEvent.preventDefault(); } this.submit(); });
		}
		let tmpVerify = document.getElementById('Retold-Login-Verify');
		if (tmpVerify && tmpVerify.getAttribute('data-retold-wired') !== '1')
		{
			tmpVerify.setAttribute('data-retold-wired', '1');
			tmpVerify.addEventListener('submit', (pEvent) => { if (pEvent && pEvent.preventDefault) { pEvent.preventDefault(); } this.submitVerify(); });
			let tmpResend = document.getElementById('Retold-Login-Resend');
			if (tmpResend) { tmpResend.addEventListener('click', (pEvent) => { if (pEvent && pEvent.preventDefault) { pEvent.preventDefault(); } this.resendCode(); }); }
			let tmpBack = document.getElementById('Retold-Login-Back');
			if (tmpBack) { tmpBack.addEventListener('click', (pEvent) => { if (pEvent && pEvent.preventDefault) { pEvent.preventDefault(); } this._showLogin(); }); }
		}
	}

	_setError(pMessage)
	{
		if (typeof document === 'undefined') { return; }
		let tmpError = document.getElementById('Retold-Login-Error');
		if (tmpError) { tmpError.textContent = pMessage ? String(pMessage) : ''; }
	}

	_setVerifyError(pMessage)
	{
		if (typeof document === 'undefined') { return; }
		let tmpError = document.getElementById('Retold-Login-Verify-Error');
		if (tmpError) { tmpError.textContent = pMessage ? String(pMessage) : ''; }
	}

	// Show the code card and describe where the code went. The two cards are swapped rather
	// than re-rendered so the wiring above stays attached.
	_showVerify(pChallenge)
	{
		if (typeof document === 'undefined') { return; }
		let tmpForm = document.getElementById('Retold-Login-Form');
		let tmpVerify = document.getElementById('Retold-Login-Verify');
		let tmpDest = document.getElementById('Retold-Login-Verify-Dest');
		if (tmpForm) { tmpForm.hidden = true; }
		if (tmpVerify) { tmpVerify.hidden = false; }
		if (tmpDest)
		{
			let tmpWhere = (pChallenge && pChallenge.MaskedDestination) ? pChallenge.MaskedDestination : 'your ' + ((pChallenge && pChallenge.Channel === 'sms') ? 'phone' : 'email');
			tmpDest.textContent = 'We sent a code to ' + tmpWhere + '. Enter it to finish signing in.';
		}
		this._setVerifyError('');
		let tmpCode = document.getElementById('Retold-Login-Code');
		if (tmpCode) { tmpCode.value = ''; tmpCode.focus(); }
	}

	_showLogin()
	{
		if (typeof document === 'undefined') { return; }
		let tmpForm = document.getElementById('Retold-Login-Form');
		let tmpVerify = document.getElementById('Retold-Login-Verify');
		if (tmpVerify) { tmpVerify.hidden = true; }
		if (tmpForm) { tmpForm.hidden = false; }
		let tmpAuthClient = this._authClient();
		if (tmpAuthClient && typeof tmpAuthClient.cancelChallenge === 'function') { tmpAuthClient.cancelChallenge(); }
		this._setVerifyError('');
	}

	/**
	 * Read the form and attempt a sign-in. Exposed so a test (or an app) can drive it
	 * without synthesizing a DOM submit event.
	 */
	submit()
	{
		if (typeof document === 'undefined') { return Promise.resolve(null); }
		let tmpUser = document.getElementById('Retold-Login-User');
		let tmpPass = document.getElementById('Retold-Login-Pass');
		let tmpUserName = tmpUser ? tmpUser.value : '';
		let tmpPassword = tmpPass ? tmpPass.value : '';

		this._setError('');
		let tmpAuthClient = this._authClient();
		if (!tmpAuthClient) { this._setError('No auth client registered.'); return Promise.resolve(null); }

		return tmpAuthClient.login(tmpUserName, tmpPassword).then((pResult) =>
		{
			if (pResult && pResult.LoggedIn)
			{
				if (typeof this.options.OnAuthenticated === 'function') { this.options.OnAuthenticated(pResult); }
				return pResult;
			}
			if (pResult && pResult.Challenge)
			{
				this._showVerify(pResult);
				return pResult;
			}
			this._setError((pResult && pResult.Error) || 'Sign in failed. Check your username and password.');
			return pResult;
		});
	}

	// Verify the two-step code and complete the sign-in.
	submitVerify()
	{
		if (typeof document === 'undefined') { return Promise.resolve(null); }
		let tmpCodeInput = document.getElementById('Retold-Login-Code');
		let tmpCode = tmpCodeInput ? String(tmpCodeInput.value || '').trim() : '';
		this._setVerifyError('');
		let tmpAuthClient = this._authClient();
		if (!tmpAuthClient || typeof tmpAuthClient.verifyLoginCode !== 'function') { this._setVerifyError('Verification is not available.'); return Promise.resolve(null); }

		return tmpAuthClient.verifyLoginCode(tmpCode).then((pResult) =>
		{
			if (pResult && pResult.LoggedIn)
			{
				if (typeof this.options.OnAuthenticated === 'function') { this.options.OnAuthenticated(pResult); }
				return pResult;
			}
			this._setVerifyError((pResult && pResult.Error) || 'That code is not right. Try again.');
			return pResult;
		});
	}

	// Ask the server to reissue the code.
	resendCode()
	{
		let tmpAuthClient = this._authClient();
		if (!tmpAuthClient || typeof tmpAuthClient.resendLoginCode !== 'function') { return Promise.resolve(null); }
		this._setVerifyError('');
		return tmpAuthClient.resendLoginCode().then((pResult) =>
		{
			let tmpDest = document.getElementById('Retold-Login-Verify-Dest');
			if (tmpDest && pResult && pResult.Resent) { tmpDest.textContent = 'A new code is on its way' + (pResult.MaskedDestination ? (' to ' + pResult.MaskedDestination) : '') + '.'; }
			return pResult;
		});
	}
}

module.exports = PictViewRetoldLogin;
module.exports.default_configuration =
{
	ViewIdentifier: 'Retold-Login',
	DefaultRenderable: 'Retold-Login',
	DefaultDestinationAddress: '#App-Container',
	AutoRender: false,
	// Every themed background here is paired with a themed foreground; the fallbacks are
	// light, so an unthemed host still reads. Inputs and buttons set their own
	// background/foreground as a pair rather than half of one.
	CSS: /*css*/ `
		.retold-login { display: flex; align-items: center; justify-content: center; height: 100%;
			background: var(--theme-color-background-primary, #f6f7f9);
			color: var(--theme-color-text-primary, #1f2430); }
		.retold-login-form { display: flex; flex-direction: column; gap: 12px; min-width: 300px; padding: 28px;
			border-radius: 10px; background: var(--theme-color-background-panel, #ffffff);
			color: var(--theme-color-text-primary, #1f2430);
			box-shadow: 0 2px 16px rgba(0,0,0,0.12); }
			/* The verify card carries the [hidden] attribute until a two-step challenge is issued;
			   the display:flex above would otherwise defeat it and show both cards at once. */
			.retold-login-form[hidden] { display: none; }
		.retold-login-form h1 { font-size: 18px; margin: 0 0 4px 0; color: inherit; }
		.retold-login-form label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: inherit; }
		.retold-login-form input { padding: 8px 10px; border-radius: 6px; font-size: 14px;
			background: var(--theme-color-background-primary, #ffffff);
			color: var(--theme-color-text-primary, #1f2430);
			border: 1px solid var(--theme-color-border-default, #d6d9de); }
		.retold-login-form button { padding: 9px 12px; border: 0; border-radius: 6px; cursor: pointer; font-size: 14px;
			background: var(--theme-color-brand-primary, #3a6ea5); color: var(--theme-color-text-on-brand, #ffffff); }
		.retold-login-hint { margin: 0; font-size: 13px; color: var(--theme-color-text-secondary, #55606f); }
		.retold-login-verify-actions { display: flex; gap: 16px; font-size: 13px; }
		.retold-login-verify-actions a { color: var(--theme-color-brand-primary, #3a6ea5); cursor: pointer; text-decoration: underline; }
		.retold-login-error { min-height: 18px; margin: 0; font-size: 13px;
			color: var(--theme-color-status-error, #c0392b); }
	`,
	Templates:
	[
		{
			Hash: 'Retold-Login',
			Template: '<div class="retold-login">' +
				'<form id="Retold-Login-Form" class="retold-login-form">' +
					'<h1 id="Retold-Login-Title">Sign in</h1>' +
					'<label>Username<input id="Retold-Login-User" type="text" autocomplete="username"></label>' +
					'<label>Password<input id="Retold-Login-Pass" type="password" autocomplete="current-password"></label>' +
					'<button type="submit">Sign in</button>' +
					'<p id="Retold-Login-Error" class="retold-login-error"></p>' +
				'</form>' +
				'<form id="Retold-Login-Verify" class="retold-login-form" hidden>' +
					'<h1>Enter your code</h1>' +
					'<p id="Retold-Login-Verify-Dest" class="retold-login-hint"></p>' +
					'<label>Verification code<input id="Retold-Login-Code" type="text" inputmode="numeric" autocomplete="one-time-code"></label>' +
					'<button type="submit">Verify and sign in</button>' +
					'<div class="retold-login-verify-actions">' +
						'<a id="Retold-Login-Resend">Resend code</a>' +
						'<a id="Retold-Login-Back">Back</a>' +
					'</div>' +
					'<p id="Retold-Login-Verify-Error" class="retold-login-error"></p>' +
				'</form>' +
				'</div>'
		}
	],
	Renderables:
	[
		{ RenderableHash: 'Retold-Login', TemplateHash: 'Retold-Login', ContentDestinationAddress: '#App-Container' }
	]
};
